import { Capacitor } from '@capacitor/core';
import { CapacitorUpdater } from '@capgo/capacitor-updater';

/**
 * Descobre a versão NATIVA (da loja) que este shell está rodando.
 *
 * ⚠️ O ponto delicado desta feature: `@capacitor/app` **não existe nos shells
 * 1.1.x** (entrou no trem nativo 1.2 — ver o aviso em `deepLinks.ts`), e são
 * justamente os shells velhos que queremos avisar. Por isso a cadeia tem dois
 * degraus: `App.getInfo()` e, se ele não estiver lá,
 * `CapacitorUpdater.current().native` — que existe em todo shell que tem OTA
 * (iOS ≥ 1.0.1, Android ≥ 1.1.x) e devolve a versão original da loja.
 *
 * Shells sem NENHUM dos dois (iOS 1.0 build 16, Android 1.0.11) não têm o
 * plugin de OTA no binário — nunca receberiam este código de qualquer forma.
 *
 * Regras que valem aqui (CLAUDE.md §10):
 *  - `try/catch` externo E timeout: plugin ausente REJEITA a Promise, e plugin
 *    quebrado pode nunca liquidá-la. Sem os dois, o app congela no boot.
 *  - Nunca `await` o proxy do plugin em si — só `import()` e chamada de método.
 *  - Falha = `null` = versão desconhecida = **nenhum aviso**. Falhar aberto é
 *    obrigatório: o pior defeito possível aqui é trancar cliente fora do app.
 */

export interface NativeVersionInfo {
  platform: 'ios' | 'android';
  /** iOS: "1.2.0" (MARKETING_VERSION) · Android: "1.2.14" (versionName). */
  version: string;
  /** Bundle OTA em execução ("builtin" se nunca atualizou). Diagnóstico. */
  bundle: string | null;
  source: 'app-plugin' | 'updater';
}

const PLUGIN_TIMEOUT_MS = 3000;

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('plugin-timeout')), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      }
    );
  });
}

/** Só aceita o que dá pra comparar: dígitos separados por ponto. Espelha o
 *  regex do backend — versão ilegível vira `null` (falha aberta) em vez de
 *  virar (0,) e disparar bloqueio falso. */
function usableVersion(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const v = raw.trim();
  return /^\d+(\.\d+)*$/.test(v) ? v : null;
}

let cached: Promise<NativeVersionInfo | null> | null = null;

async function resolveNativeVersion(): Promise<NativeVersionInfo | null> {
  // PWA web se atualiza sozinha pelo Service Worker (`main.tsx`) — não há loja
  // pra onde mandar ninguém.
  if (!Capacitor.isNativePlatform()) return null;

  const platform = Capacitor.getPlatform();
  if (platform !== 'ios' && platform !== 'android') return null;

  // Degrau 1 — shells 1.2+.
  if (Capacitor.isPluginAvailable('App')) {
    try {
      const { App } = await import('@capacitor/app');
      const info = await withTimeout(App.getInfo(), PLUGIN_TIMEOUT_MS);
      const version = usableVersion(info?.version);
      if (version) {
        return { platform, version, bundle: null, source: 'app-plugin' };
      }
    } catch {
      // Cai no degrau 2.
    }
  }

  // Degrau 2 — é ESTE que alcança os shells 1.1.x.
  //
  // Sem gate de `isPluginAvailable` de propósito: este é o ÚNICO caminho até os
  // shells velhos, e um falso negativo do gate (nome registrado diferente,
  // shell exótico) o mataria em silêncio. Plugin ausente apenas rejeita — e a
  // rejeição já está tratada aqui, com timeout pro caso de nunca liquidar.
  try {
    const current = await withTimeout(
      CapacitorUpdater.current(),
      PLUGIN_TIMEOUT_MS
    );
    const version = usableVersion(current?.native);
    if (version) {
      return {
        platform,
        version,
        bundle: current?.bundle?.version ?? null,
        source: 'updater'
      };
    }
  } catch {
    // Sem versão confiável — desiste.
  }

  return null;
}

/** Memoizado: uma resolução por abertura do app (dois consumidores — o gate
 *  duro e a faixa soft — não podem custar duas idas ao plugin). */
export function getNativeVersion(): Promise<NativeVersionInfo | null> {
  if (cached === null) {
    cached = resolveNativeVersion().catch(() => null);
  }
  return cached;
}

/** Só para testes. */
export function __resetNativeVersionCache(): void {
  cached = null;
}
